import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/browser';
import { useUserContext } from '../../contexts/UserContext';
import {
    listAllLibraryPlanTemplates,
    updateLibraryPlanTemplate,
    deleteLibraryPlanTemplate as deleteLibraryPlanTemplateMutation,
} from '../../graphql/resourceLibraryQueries';
import { LibraryPlanTemplate, LibraryPlanCategory } from '../../types/resourceLibraryTypes';
import { AssignmentModal } from './AssignmentModal';
import { PlanTemplateEditorModal } from './PlanTemplateEditorModal';
import { DeletePlanModal } from './DeletePlanModal';
import { Btn, BtnIconOnly, IconEdit, IconMenu, IconPlus, IconTrashEmpty, TEIQspinner } from '../ui';
import '../../css/ResourceLibraryContent.css';

/** Parse the categories JSON string into an array of LibraryPlanCategory. */
function parseCategories(categoriesJson: string): LibraryPlanCategory[] {
    try {
        return JSON.parse(categoriesJson) as LibraryPlanCategory[];
    } catch {
        return [];
    }
}

/** Compute total scheduled minutes from all categories. */
function computeTotalMinutes(categories: LibraryPlanCategory[]): number {
    return categories.reduce((sum, cat) => sum + (cat.topicRefs ?? []).reduce((s, t) => s + (t.timeValue ?? 0), 0), 0);
}

/** Compute total topic count from all categories. */
function computeTopicCount(categories: LibraryPlanCategory[]): number {
    return categories.reduce((sum, cat) => sum + (cat.topicRefs ?? []).length, 0);
}

/** Build a subtitle string for a plan row. */
function buildSubtitle(categories: LibraryPlanCategory[]): string {
    const catCount = categories.length;
    const topicCount = computeTopicCount(categories);
    return `${catCount} ${catCount === 1 ? 'category' : 'categories'}, ${topicCount} ${topicCount === 1 ? 'topic' : 'topics'}`;
}

export const SafetyPlanTemplatesTab: React.FC = () => {
    const { companyOwner } = useUserContext();
    const navigate = useNavigate();

    const [plans, setPlans] = useState<LibraryPlanTemplate[]>([]);
    const [loading, setLoading] = useState(true);

    // Modal state
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState<LibraryPlanTemplate | null>(null);
    const [deletePlan, setDeletePlan] = useState<LibraryPlanTemplate | null>(null);
    const [assignPlan, setAssignPlan] = useState<LibraryPlanTemplate | null>(null);

    const fetchPlans = useCallback(async () => {
        setLoading(true);
        try {
            const all = (await listAllLibraryPlanTemplates()) as LibraryPlanTemplate[];
            setPlans(all);
        } catch (err) {
            console.error('Error fetching plan templates:', err);
            Sentry.captureException(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    // --- Handlers ---
    const handleAddClick = (buttonType: 'addTemplate' | 'topicList') => {
        if (buttonType === 'addTemplate') {
            return setEditorOpen(true);
        }
        navigate('/admin-training?view=topics');
    };

    const handleEditClick = (plan: LibraryPlanTemplate) => {
        setEditingPlan(plan);
        setEditorOpen(true);
    };

    const handleEditorClose = () => {
        setEditorOpen(false);
        setEditingPlan(null);
    };

    const handleEditorSave = () => {
        setEditorOpen(false);
        setEditingPlan(null);
        fetchPlans();
    };

    const handleDeleteClick = (plan: LibraryPlanTemplate) => {
        setDeletePlan(plan);
    };

    const handleDeleteClose = () => {
        setDeletePlan(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deletePlan) return;
        try {
            await deleteLibraryPlanTemplateMutation(deletePlan.id);
            setDeletePlan(null);
            fetchPlans();
        } catch (err) {
            console.error('Error deleting plan template:', err);
            Sentry.captureException(err);
            throw err;
        }
    };

    const handleAssignClick = (plan: LibraryPlanTemplate) => {
        setAssignPlan(plan);
    };

    const handleAssignSave = async (companyIds: string[]) => {
        if (!assignPlan) return;
        await updateLibraryPlanTemplate({
            id: assignPlan.id,
            sharedWithCompanyIds: companyIds,
        });
        fetchPlans();
    };

    if (loading) {
        return (
            <div className="zrl-loading">
                <TEIQspinner label="Loading safety plan templates" />
            </div>
        );
    }

    return (
        <>
            {/* Section Header */}
            <div className="zrl-section-header">
                <h2 className="zrl-section-title">
                    Safety Plan Templates
                    <span className="zrl-count-badge">{plans.length}</span>
                </h2>
                <div className="zrl-section-buttons">
                    <Btn
                        variant="secondary-outlined"
                        buttonSize="sm"
                        buttonIcon={<IconMenu />}
                        buttonText="Super Admin Master Topic List"
                        buttonOnClick={() => handleAddClick('topicList')}
                    />
                    <Btn
                        variant="primary"
                        buttonSize="sm"
                        buttonIcon={<IconPlus />}
                        buttonText="Add Template"
                        buttonOnClick={() => handleAddClick('addTemplate')}
                    />
                </div>
            </div>

            {/* Table */}
            {plans.length === 0 ? (
                <div className="zrl-empty">No plan templates yet. Click "+ Add Template" to get started.</div>
            ) : (
                <div className="zrl-table-wrapper">
                    <table className="zrl-table">
                        <thead>
                            <tr>
                                <th>Template Name</th>
                                <th>Standard</th>
                                <th>Topics</th>
                                <th>Categories</th>
                                <th>Total Duration</th>
                                <th>Status</th>
                                <th>Assign to Companies</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plans.map((plan) => {
                                const categories = parseCategories(plan.categories);
                                const topicCount = plan.topicCount ?? computeTopicCount(categories);
                                const categoryCount = plan.categoryCount ?? categories.length;
                                const totalMinutes = computeTotalMinutes(categories);
                                const isDraft = plan.status === 'DRAFT';

                                return (
                                    <tr key={plan.id}>
                                        <td>
                                            <div className="zrl-table-name">{plan.name}</div>
                                            <div className="zrl-table-desc">{buildSubtitle(categories)}</div>
                                        </td>
                                        <td>
                                            <span
                                                // TO DO: we may want this to be dynamic based on deadlines, configurable, etc.
                                                className={`zrl-badge ${plan.governingStandard === 'MSHA' ? 'zrl-badge-msha' : 'zrl-badge-standard'}`}
                                            >
                                                {plan.governingStandard}
                                            </span>
                                        </td>
                                        <td>{topicCount}</td>
                                        <td>{categoryCount}</td>
                                        <td>{totalMinutes} min</td>
                                        <td>
                                            <span
                                                className={`zrl-badge ${isDraft ? 'zrl-badge-draft' : 'zrl-badge-active'}`}
                                            >
                                                {isDraft ? 'Draft' : 'Active'}
                                            </span>
                                        </td>
                                        <td>
                                            <Btn
                                                variant="secondary-outlined"
                                                buttonSize="sm"
                                                buttonText="Manage Assignment"
                                                buttonDisabled={isDraft}
                                                buttonOnClick={() => handleAssignClick(plan)}
                                            />
                                        </td>
                                        <td>
                                            <div className="zrl-actions">
                                                <BtnIconOnly
                                                    size="sm"
                                                    icon={<IconEdit />}
                                                    ariaLabel={`Edit ${plan.name}`}
                                                    onClick={() => handleEditClick(plan)}
                                                />
                                                <BtnIconOnly
                                                    size="sm"
                                                    variant="danger"
                                                    icon={<IconTrashEmpty />}
                                                    ariaLabel={`Delete ${plan.name}`}
                                                    onClick={() => handleDeleteClick(plan)}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Plan Template Editor Modal */}
            <PlanTemplateEditorModal
                isOpen={editorOpen}
                onClose={handleEditorClose}
                onSave={handleEditorSave}
                editingPlan={editingPlan}
                adminId={companyOwner?.id || ''}
                adminName={companyOwner?.name || ''}
            />

            {/* Delete Plan Modal */}
            {deletePlan && (
                <DeletePlanModal
                    isOpen={!!deletePlan}
                    onClose={handleDeleteClose}
                    onDelete={handleDeleteConfirm}
                    planName={deletePlan.name}
                    standard={deletePlan.governingStandard}
                    categoryCount={deletePlan.categoryCount ?? parseCategories(deletePlan.categories).length}
                    topicCount={deletePlan.topicCount ?? computeTopicCount(parseCategories(deletePlan.categories))}
                    assignmentCount={deletePlan.sharedWithCompanyIds?.filter(Boolean).length ?? 0}
                />
            )}

            {/* Assignment Modal */}
            {assignPlan && (
                <AssignmentModal
                    isOpen={!!assignPlan}
                    onClose={() => setAssignPlan(null)}
                    resourceName={assignPlan.name}
                    resourceType="plan"
                    currentCompanyIds={(assignPlan.sharedWithCompanyIds?.filter(Boolean) as string[]) || []}
                    onSave={handleAssignSave}
                    subtitle="When assigned, companies receive a full editable copy of this plan — topics, sequence, and all."
                />
            )}
        </>
    );
};
